import { useEffect, useRef, useState } from "react";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import CommonTable from "../component/CommonTable";
import axios from "axios";
import MessageToster from "../component/MessageToster";
import { BASE_URL } from "../api";
import Loader from "../component/Loader";
import { closeModal } from "../utils/modal";
import { useHandleError } from "../hooks/useHandleError";
import { getApiHeaders } from "../utils/helpers";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import { exportToExcel } from "../utils/exportData";
import ExportButton from "../component/ExportButton";
import MobileFilter from "../component/MobileFilter";
import CustomSelect from "../component/CustomSelect";

// New Ticket dropdown options
const PRIORITY_OPTIONS = ["Low", "Normal", "High", "Urgent"];
const DEPARTMENT_OPTIONS = ["Support", "Deposit", "Withdraw", "Partners", "Live Account", "Other"];

// Attachment rules (same as KYC upload): jpeg / jpg / png / pdf, max 5MB
const ATTACHMENT_MAX_SIZE_MB = 5;
const ATTACHMENT_TYPES = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
const ATTACHMENT_EXT_REGEX = /\.(jpe?g|png|pdf)$/i;

const isAllowedAttachment = (file) =>
  ATTACHMENT_TYPES.includes(file.type) || ATTACHMENT_EXT_REGEX.test(file.name || "");

const isPdfFile = (file) =>
  Boolean(file) && (file.type === "application/pdf" || /\.pdf$/i.test(file.name || ""));

export default function Tickets() {
  const fileUpload = useRef(null);
  const handleError = useHandleError();

  const [input, setInput] = useState({
    title: "",
    priority: "",
    department: "",
    note: "",
    attachment: ""
  });
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    status: "All"
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  // const [verror, setVerror] = useState("");

  const [validations, setValidations] = useState({
    title: "",
    priority: "",
    department: "",
    note: "",
    attachment: ""
  });

  // Object URL of the chosen attachment, used for the preview inside the New Ticket modal
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  // Free the previous preview URL whenever it changes and when the page is left
  useEffect(() => {
    return () => {
      if (attachmentPreview) URL.revokeObjectURL(attachmentPreview);
    };
  }, [attachmentPreview]);

  const validationRules = {
    title: "Subject is required.",
    priority: "Priority is required.",
    department: "Department is required.",
    note: "Description is required.",
    attachment: "Attachment is required."
  };

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;

    if (type === "file" && files.length > 0) {
      setInput((prev) => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setInput((prev) => ({
        ...prev,
        [name]: value
      }));
    }

    setValidations((prev) => ({
      ...prev,
      [name]: false
    }));
  };

  const clearAttachment = (errorMessage = "") => {
    setAttachmentPreview(null);
    setInput((prev) => ({ ...prev, attachment: "" }));
    setValidations((prev) => ({ ...prev, attachment: errorMessage }));
    if (fileUpload.current) {
      fileUpload.current.value = "";
    }
  };

  const handleAttachmentChange = (e) => {
    const file = e.target.files && e.target.files[0];

    // File picker was cancelled
    if (!file) {
      clearAttachment();
      return;
    }

    if (!isAllowedAttachment(file)) {
      const message = "Only JPG, JPEG, PNG, and PDF files are allowed.";
      MessageToster("error", message);
      clearAttachment(message);
      return;
    }

    if (file.size > ATTACHMENT_MAX_SIZE_MB * 1024 * 1024) {
      const message = `File size must not exceed ${ATTACHMENT_MAX_SIZE_MB}MB.`;
      MessageToster("error", message);
      clearAttachment(message);
      return;
    }

    setAttachmentPreview(URL.createObjectURL(file));
    setInput((prev) => ({ ...prev, attachment: file }));
    setValidations((prev) => ({ ...prev, attachment: "" }));
  };

  const handleReset = () => {
    setAttachmentPreview(null);
    setInput({
      title: "",
      priority: "",
      department: "",
      note: "",
      attachment: ""
    });
    setValidations({
      title: "",
      priority: "",
      department: "",
      note: "",
      attachment: ""
    });
    // setVerror("");

    if (fileUpload.current) {
      fileUpload.current.value = "";
    }
  };

  const handleSearch = () => {
    let result = [...tickets];
    if (filters.fromDate) {
      const from = new Date(filters.fromDate);
      from.setHours(0, 0, 0, 0);
      result = result.filter((item) => {
        const itemDateStr = item.ondate ? String(item.ondate).split(" ")[0] : "";
        const itemDate = new Date(itemDateStr);
        return itemDate >= from;
      });
    }
    if (filters.toDate) {
      const to = new Date(filters.toDate);
      to.setHours(23, 59, 59, 999);
      result = result.filter((item) => {
        const itemDateStr = item.ondate ? String(item.ondate).split(" ")[0] : "";
        const itemDate = new Date(itemDateStr);
        return itemDate <= to;
      });
    }
    if (filters.status && filters.status !== "All") {
      result = result.filter(
        (item) => item.status?.toLowerCase() === filters.status.toLowerCase()
      );
    }
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((item) => {
        return (
          String(item.ticketno || "").toLowerCase().includes(searchLower) ||
          String(item.priority || "").toLowerCase().includes(searchLower) ||
          String(item.department || "").toLowerCase().includes(searchLower) ||
          String(item.status || "").toLowerCase().includes(searchLower)
        );
      });
    }
    setFilteredTickets(result);
  };

  const clearFilters = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      status: "All"
    });
    setSearchTerm("");
    setFilteredTickets(tickets);
  };

  const onSubmitForm = () => {
    if (loading) return;

    // Attachment is optional; every other field is required (only spaces does not count)
    const errors = {};
    ["title", "priority", "department", "note"].forEach((field) => {
      if (!String(input[field] || "").trim()) errors[field] = true;
    });

    if (Object.keys(errors).length > 0) {
      setValidations((prev) => ({ ...prev, ...errors }));
      return;
    }
    setLoading(true);
    AddTicket();
  };

  const AddTicket = async () => {
    const dataofmid = sessionStorage.getItem('mid');
    const formData = new FormData();
    formData.append("mid", Number(dataofmid));
    formData.append("title", input.title.trim());
    formData.append("priority", input.priority);
    formData.append("department", input.department);
    formData.append("note", input.note.trim());
    if (input.attachment) {
      formData.append("attachment", input.attachment);
    }

    try {
      const res = await axios.post(`${BASE_URL}/support/raise_ticket`, formData, { headers: getApiHeaders(true) });

      if (res.data.code === 200) {
        MessageToster("success", res.data.message);
        closeModal()
        handleReset();
        getTickets();
      } else {
        MessageToster("error", res.data.message);
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const getTickets = async () => {
    setLoading(true)
    const dataofmid = sessionStorage.getItem("mid");
    const bodyData = {
      mode: "",
      mid: Number(dataofmid)
    }
    try {
      const response = await axios.post(`${BASE_URL}/support/get_all_ticket`, bodyData, { headers: getApiHeaders() });

      // if (response.data.code === 200) {
      //   setLoading(false);
      //   const raw = response.data.message;
      //   const normalized = Array.isArray(raw) ? raw : [raw];

      //   setTickets(normalized);
      // } else {
      //   setLoading(false)
      //   MessageToster("error", response.data.message)
      // }
      if (response.data.code === 200) {
        const raw = response.data.message;
        const normalized = Array.isArray(raw)
          ? raw.filter(t => t.ticketno)
          : (raw && raw.ticketno ? [raw] : []);

        setTickets(normalized);
        setFilteredTickets(normalized);
      } else if (
        response.data.code === 400 &&
        Array.isArray(response.data.message) &&
        response.data.message[0]?.msg === "No ticket found this user"
      ) {
        setTickets([]);
        setFilteredTickets([]);
        MessageToster("error", "No tickets found for this user.");

      } else {
        MessageToster("error", response.data.message);
      }

    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { getTickets(); }, []);

  // useEffect(() => {
  //   if (tickets.length > 0) {
  //     if (!window.$.fn.DataTable.isDataTable(tableRef.current)) {
  //       displayButtons(tableRef);
  //     }
  //   }
  // }, [tickets]);




  return (
    <>
      <div id="layout-wrapper">
        {loading &&
          <Loader />}
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* start page title */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-0">Tickets</h2>
                    <div className="page-title-right">
                      <button
                        type="button"
                        className="btn btn-primary waves-effect waves-light"
                        data-bs-toggle="modal"
                        data-bs-target=".bs-example-modal-center"
                        onClick={handleReset}
                      >
                        New Ticket
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* end page title */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                        <div>
                          <label htmlFor="">From</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="fromDate"
                            value={filters.fromDate}
                            max={filters.toDate || new Date().toISOString().split("T")[0]}
                            onChange={(e) => setFilters(prev => ({...prev, fromDate: e.target.value}))}
                          />
                        </div>
                        <div>
                          <label htmlFor="">To</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="toDate"
                            value={filters.toDate}
                            min={filters.fromDate || undefined}
                            max={new Date().toISOString().split("T")[0]}
                            onChange={(e) => setFilters(prev => ({...prev, toDate: e.target.value}))}
                          />
                        </div>
                        <div>
                          <label htmlFor="">Status</label>
                          <select
                            className="form-select"
                            name="status"
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                          >
                            <option value="All">All</option>
                            <option value="Open">Open</option>
                            <option value="Replied">Replied</option>
                            <option value="Close">Close</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            onClick={handleSearch}
                          >
                            Search
                          </button>
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light"
                            onClick={clearFilters}
                            disabled={!filters.fromDate && !filters.toDate && filters.status === "All" && !searchTerm}
                          >
                            Clear
                          </button>
                        </div>
                        <div className="ms-auto">
                          <ExportButton
                            onClick={() => exportToExcel(filteredTickets, "tickets_report", "tickets")}
                          />
                        </div>
                      </div>

                      <MobileFilter
                        title="History"
                        onExport={() => exportToExcel(filteredTickets, "tickets_report", "tickets")}
                      >
                        <div>
                          <label htmlFor="">From</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="fromDate"
                            value={filters.fromDate}
                            max={filters.toDate || new Date().toISOString().split("T")[0]}
                            onChange={(e) => setFilters(prev => ({...prev, fromDate: e.target.value}))}
                          />
                        </div>
                        <div>
                          <label htmlFor="">To</label>
                          <input
                            className="form-control"
                            type="date"
                            onKeyDown={(e) => e.preventDefault()}
                            name="toDate"
                            value={filters.toDate}
                            min={filters.fromDate || undefined}
                            max={new Date().toISOString().split("T")[0]}
                            onChange={(e) => setFilters(prev => ({...prev, toDate: e.target.value}))}
                          />
                        </div>
                        <div>
                          <label htmlFor="">Status</label>
                          <select
                            className="form-select"
                            name="status"
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                          >
                            <option value="All">All</option>
                            <option value="Open">Open</option>
                            <option value="Replied">Replied</option>
                            <option value="Close">Close</option>
                          </select>
                        </div>
                        <div>
                          <label htmlFor="">Search</label>
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>
                        <div className="d-flex gap-2 mt-2">
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light flex-grow-1"
                            onClick={handleSearch}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Search
                          </button>
                          <button
                            type="button"
                            className="btn btn-light waves-effect waves-light border flex-grow-1"
                            onClick={clearFilters}
                            disabled={!filters.fromDate && !filters.toDate && filters.status === "All" && !searchTerm}
                            data-bs-dismiss="offcanvas"
                            data-bs-target="#filterOffcanvasMobile"
                          >
                            Clear
                          </button>
                        </div>
                      </MobileFilter>
                      <CommonTable
                        columns={[
                          { header: "Ticket No", accessor: "ticketno" },
                          { header: "Priority", accessor: "priority" },
                          { header: "Department", accessor: "department" },
                          { header: "Created Date", render: (row) => row.ondate ? formatDateDDMMYYYY(row.ondate) : "" },
                          { header: "Closed Date", render: (row) => row.closed_date ? formatDateDDMMYYYY(row.closed_date) : "-" },
                          {
                            header: "Status",
                            render: (row) => (
                              <button className={`btn btn-sm ${row.status === "Open"
                                ? "btn-soft-success"
                                : row.status === "Replied"
                                  ? "btn-soft-warning"
                                  : row.status === "Close"
                                    ? "btn-soft-danger"
                                    : ""
                                }`}>{row.status}</button>
                            )
                          },
                          {
                            header: "Action",
                            render: (row) => (
                              <a href={`/ticketinformation?id=${row.id}`} className="btn btn-sm btn-soft-primary">
                                <i className="mdi mdi-eye-outline fs-5"></i>
                              </a>
                            )
                          }
                        ]}
                        data={filteredTickets}
                        loading={loading}
                      />
                    </div>
                  </div>
                </div>{" "}
                {/* end col */}
                <div>
                  {/* modal */}
                  <div
                    className="modal fade bs-example-modal-center"
                    tabIndex={-1}
                    role="dialog"
                    aria-hidden="true"
                  >
                    <div className="modal-dialog modal-dialog-centered">
                      <div className="modal-content">
                        <div className="modal-header">
                          <h5 className="modal-title"> New Ticket</h5>
                          <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            aria-label="Close"
                            onClick={handleReset}
                          />
                        </div>

                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            onSubmitForm();
                          }}
                        >
                          <div className="modal-body">
                            <div className="row">
                              {/* Title */}
                              <div className="col-12 mb-3">
                                <label className="form-label">
                                  Subject <span className="text-danger">*</span>
                                </label>
                                <input
                                  className="form-control"
                                  type="text"
                                  name="title"
                                  placeholder="Enter subject"
                                  value={input.title}
                                  onChange={handleChange}
                                />
                                {validations.title && (
                                  <p className="text-danger font-size-13 d-flex align-items-center gap-1 mt-1 mb-0">
                                    <i className="bx bx-info-circle font-size-16 flex-shrink-0" />
                                    <span>{validationRules.title}</span>
                                  </p>
                                )}
                              </div>

                              {/* Priority */}
                              <div className="col-12 mb-3">
                                <label className="form-label">
                                  Priority <span className="text-danger">*</span>
                                </label>
                                <CustomSelect
                                  name="priority"
                                  value={input.priority}
                                  options={PRIORITY_OPTIONS}
                                  onChange={handleChange}
                                  placeholder="Select an Option"
                                  hasError={!!validations.priority}
                                />
                                {validations.priority && (
                                  <p className="text-danger font-size-13 d-flex align-items-center gap-1 mt-1 mb-0">
                                    <i className="bx bx-info-circle font-size-16 flex-shrink-0" />
                                    <span>{validationRules.priority}</span>
                                  </p>
                                )}
                              </div>

                              {/* Department */}
                              <div className="col-12 mb-3">
                                <label className="form-label">
                                  Department <span className="text-danger">*</span>
                                </label>
                                <CustomSelect
                                  name="department"
                                  value={input.department}
                                  options={DEPARTMENT_OPTIONS}
                                  onChange={handleChange}
                                  placeholder="Select an Option"
                                  hasError={!!validations.department}
                                />
                                {validations.department && (
                                  <p className="text-danger font-size-13 d-flex align-items-center gap-1 mt-1 mb-0">
                                    <i className="bx bx-info-circle font-size-16 flex-shrink-0" />
                                    <span>{validationRules.department}</span>
                                  </p>
                                )}
                              </div>

                              {/* Description */}
                              <div className="col-12 mb-3">
                                <label className="form-label">
                                  Description <span className="text-danger">*</span>
                                </label>
                                <textarea
                                  className="form-control"
                                  name="note"
                                  rows={3}
                                  placeholder="Enter description"
                                  value={input.note}
                                  onChange={handleChange}
                                />
                                {validations.note && (
                                  <p className="text-danger font-size-13 d-flex align-items-center gap-1 mt-1 mb-0">
                                    <i className="bx bx-info-circle font-size-16 flex-shrink-0" />
                                    <span>{validationRules.note}</span>
                                  </p>
                                )}
                              </div>

                              {/* Attachment */}
                              <div className="col-12">
                                <label className="form-label">Attachment</label>
                                <input
                                  id="inputGroupFile02"
                                  type="file"
                                  name="attachment"
                                  className="form-control"
                                  accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,image/jpg,application/pdf"
                                  onChange={handleAttachmentChange}
                                  ref={fileUpload}
                                />

                                {/* Preview of the chosen file with a cancel (x) button */}
                                {attachmentPreview && input.attachment && (
                                  <div className="mt-3">
                                    {isPdfFile(input.attachment) ? (
                                      <div
                                        className="position-relative d-inline-flex align-items-center gap-2 p-2 bg-light border rounded"
                                        style={{ maxWidth: "240px" }}
                                      >
                                        <a
                                          href={attachmentPreview}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="d-flex align-items-center gap-2 text-decoration-none text-dark overflow-hidden"
                                          title="Click to view PDF"
                                        >
                                          <i className="bx bxs-file-pdf font-size-28 text-danger flex-shrink-0" />
                                          <div className="overflow-hidden text-start">
                                            <span
                                              className="fw-semibold font-size-12 d-block text-truncate"
                                              style={{ maxWidth: "150px" }}
                                              title={input.attachment.name}
                                            >
                                              {input.attachment.name || "Document.pdf"}
                                            </span>
                                            <small className="text-muted font-size-11">
                                              {(input.attachment.size / (1024 * 1024)).toFixed(2)} MB
                                            </small>
                                          </div>
                                        </a>
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-sm rounded-circle p-0 position-absolute"
                                          style={{
                                            top: "-8px",
                                            right: "-8px",
                                            width: "22px",
                                            height: "22px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                                            border: "2px solid #fff",
                                            cursor: "pointer",
                                            zIndex: 5
                                          }}
                                          onClick={() => clearAttachment()}
                                          title="Cancel / Remove"
                                        >
                                          <i className="bx bx-x font-size-16" />
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="position-relative d-inline-block">
                                        <a href={attachmentPreview} target="_blank" rel="noopener noreferrer" title="Click to view image">
                                          <img
                                            src={attachmentPreview}
                                            alt="Attachment Preview"
                                            className="rounded border"
                                            style={{ width: "100px", height: "70px", objectFit: "cover", display: "block" }}
                                          />
                                        </a>
                                        <button
                                          type="button"
                                          className="btn btn-danger btn-sm rounded-circle p-0 position-absolute"
                                          style={{
                                            top: "-8px",
                                            right: "-8px",
                                            width: "22px",
                                            height: "22px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                                            border: "2px solid #fff",
                                            cursor: "pointer",
                                            zIndex: 5
                                          }}
                                          onClick={() => clearAttachment()}
                                          title="Cancel / Remove"
                                        >
                                          <i className="bx bx-x font-size-16" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}

                                <p className="mt-2 text-primary mb-0">
                                  File Supported: jpeg, jpg, png, pdf &amp; max File size: {ATTACHMENT_MAX_SIZE_MB}MB
                                </p>
                                {validations.attachment && (
                                  <p className="text-danger font-size-13 d-flex align-items-center gap-1 mt-1 mb-0">
                                    <i className="bx bx-info-circle font-size-16 flex-shrink-0" />
                                    <span>{validations.attachment}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="modal-footer">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={handleReset}
                            >
                              Reset
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={loading}>
                              Submit
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>

                  {/* /.modal */}
                </div>
              </div>
            </div>
            {/* container-fluid */}
          </div>

          {/* End Page-content */}
          <Footer />
        </div>
      </div>
    </>
  );
}
